import { IGenPart } from '../genPart';
import { PageGen, PdfRect, PdfUnit, PdfPoint, PdfPen, PdfBrush, PdfColor } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';

/**
 * Draws an analog clock face.
 * Data format: "HH:MM" (e.g. "11:30", "3:15")
 */
export class ClockPart implements IGenPart {
  draw(page: PageGen, rect: PdfRect, data: string): void {
    const parts = data.split(':');
    const hours = parseInt(parts[0], 10) % 12;
    const minutes = parseInt(parts[1] ?? '0', 10);

    // Center and radius
    const size = Math.min(rect.width, rect.height);
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const radius = (size / 2) * 0.9;

    // Clock face circle
    const faceRect = new PdfRect(cx - radius, cy - radius, radius * 2, radius * 2);
    page.drawEllipse(GUtil.basePen, new PdfBrush(PdfColor.fromRgb(255, 255, 255)), faceRect);

    // Tick marks at hour positions
    const tickPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
    for (let h = 0; h < 12; h++) {
      const angle = ((h * 30 - 90) * Math.PI) / 180;
      const outerR = radius * 0.92;
      const innerR = radius * 0.78;
      const x1 = cx + Math.cos(angle) * innerR;
      const y1 = cy + Math.sin(angle) * innerR;
      const x2 = cx + Math.cos(angle) * outerR;
      const y2 = cy + Math.sin(angle) * outerR;
      page.drawLine(tickPen, new PdfPoint(x1, y1), new PdfPoint(x2, y2));
    }

    // Hour hand
    const hourAngle = (((hours + minutes / 60) * 30 - 90) * Math.PI) / 180;
    const hourLen = radius * 0.5;
    const hourPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 2.5);
    page.drawLine(
      hourPen,
      new PdfPoint(cx, cy),
      new PdfPoint(cx + Math.cos(hourAngle) * hourLen, cy + Math.sin(hourAngle) * hourLen)
    );

    // Minute hand
    const minuteAngle = ((minutes * 6 - 90) * Math.PI) / 180;
    const minuteLen = radius * 0.72;
    const minutePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1.5);
    page.drawLine(
      minutePen,
      new PdfPoint(cx, cy),
      new PdfPoint(cx + Math.cos(minuteAngle) * minuteLen, cy + Math.sin(minuteAngle) * minuteLen)
    );

    // Center dot
    const dotSize = PdfUnit.FromMillimeters(1.5);
    page.drawEllipse(
      null,
      PdfBrush.Black,
      new PdfRect(cx - dotSize / 2, cy - dotSize / 2, dotSize, dotSize)
    );
  }
}
